/**
 * Shared media manager for the portal.
 *
 * Caps the number of concurrently-buffering <video> elements and disposes
 * off-screen videos by releasing their source, freeing memory and network.
 *
 * Usage:
 *   import { mediaManager } from "../lib/media-manager.js";
 *   mediaManager.register(videoElement);   // on mount
 *   mediaManager.unregister(videoElement); // on unmount
 *
 * The manager automatically pauses and releases videos when the concurrent
 * count exceeds the limit, prioritizing the most recently registered (visible)
 * videos.
 */

const MAX_CONCURRENT_BUFFERING = 3;

interface ManagedVideo {
  element: HTMLVideoElement;
  registeredAt: number;
  wasPaused: boolean;
}

class MediaManager {
  private registry = new Map<HTMLVideoElement, ManagedVideo>();

  /**
   * Register a video element for lifecycle management. Call when the
   * video mounts or starts buffering.
   */
  register(el: HTMLVideoElement): void {
    if (this.registry.has(el)) return;

    this.registry.set(el, {
      element: el,
      registeredAt: Date.now(),
      wasPaused: el.paused,
    });

    this.enforce();
  }

  /**
   * Unregister a video element. Call on unmount to release the reference
   * and free the buffered data.
   */
  unregister(el: HTMLVideoElement): void {
    this.registry.delete(el);
    this.releaseVideo(el);
  }

  /**
   * Mark a video as actively playing (bump its priority so it won't be
   * evicted by the concurrent limit).
   */
  promote(el: HTMLVideoElement): void {
    const entry = this.registry.get(el);
    if (entry) {
      entry.registeredAt = Date.now();
    }
  }

  /**
   * Enforce the concurrent buffering limit. Videos registered earliest
   * are released first.
   */
  private enforce(): void {
    if (this.registry.size <= MAX_CONCURRENT_BUFFERING) return;

    const sorted = [...this.registry.values()].sort(
      (a, b) => a.registeredAt - b.registeredAt,
    );

    const excess = sorted.length - MAX_CONCURRENT_BUFFERING;
    for (let i = 0; i < excess; i++) {
      const entry = sorted[i]!;
      entry.wasPaused = entry.element.paused;
      this.releaseVideo(entry.element);
    }
  }

  /**
   * Release a video's buffered data. Pauses playback, removes the src,
   * and calls load() to release the media decoder.
   */
  private releaseVideo(el: HTMLVideoElement): void {
    if (!el.paused) el.pause();
    el.removeAttribute("src");
    el.load();
  }

  /**
   * Dispose all managed videos. Call on app-level cleanup (e.g. route
   * transitions that unmount the entire game/video section).
   */
  disposeAll(): void {
    for (const entry of this.registry.values()) {
      this.releaseVideo(entry.element);
    }
    this.registry.clear();
  }
}

export const mediaManager = new MediaManager();
